import { Profile } from "./IUser";

/** Represents the possible actions related to a player's presence in the room.
*/
export enum LivePresenceAction {JOIN = "JOIN", LEAVE = "LEAVE", HOST_CHANGED = "HOST_CHANGED"}
export enum LiveEventDataType {
 // Player starts the game.
 START_GAME = "START_GAME"
}

export interface LiveRoomData {
    roomId: string;
    players: LivePlayerDataEvent[];
}
export interface LivePlayerDataEvent {
    profile: Profile;
    uid?: string;
    isLocalPlayer?: boolean;
    isHost?: boolean;
    action: LivePresenceAction;
}
export interface DevSdkError {}

export declare interface LiveEventData {
 // UUID to track player activities in the play session.
 // (-- Not Zwieback Id --).
 uid?: string;
 // Event data payload. It shouldn't exceed 1MB.
 payload: string;
 /**
 * The sequential order of the action taken in the current play session.
 *
 * This value is incremented globally across all players in the same session.
 * For example:
 * - Player 1 performs the first move, and `actionOrder` is 1.
 * - Player 2 performs the next move, and `actionOrder` is 2.
 * - The sequence continues, ensuring all actions are uniquely and
chronologically
 * ordered.
 *
 * This server-side assignment ensures consistency in action processing and
ordering,
 * regardless of client-side timing or network latency.
 */
 actionOrder?: number;
 // Timestamp (in milliseconds) indicating when the server received the event
 // This property is only set for events relayed by the server.
 serverTimestampMillis?: number;
 // Indicates whether the event was triggered by the local player.
 isLocalPlayer?: boolean;
 // The type of the event.
 type?: LiveEventDataType;
}

/**
* Interface for handling methods related to the live multiplayer functionality
in the
* Gamesnacks Developer SDK, enabling players to play together simultaneously.
*/
export default interface ILiveMultiplayer {
 /**
 * Creates a new private room and adds this player to it as the host.
 * The host is the only player who can start the game.
 *
 * **Host Flow:** After creating a room, an `onPlayerPresenceUpdated` event
confirms
 * the host's entry.
 *
 * This method also copies the url into the user's clipboard
 *
 * @return A Promise that resolves to the Url of the created room, or rejects
if
 * creation fails.
 */
 createRoom(): Promise<string>;
 /**
 * Registers a callback function to be triggered when a room is loaded.
 *
 * **Invitee Flow:** This event fires when a player navigates to a room URL.
 * At this stage, the player isn't yet a member and can't send/receive events
or catch
 * up.
 * They must first join using `updatePlayerPresence`.
 *
 * @param callback - The function to execute when a room has finished
 * loading. The callback receives a Promise that resolves to a LiveRoomData
object.
 */
 onRoomLoaded(
 callback: (roomDataPromise: Promise<LiveRoomData>) => void,
 ): void;
 /**
 * Updates the player's presence in the room.
 *
 * **Invitee Flow:** This method is not required for invitees. It is called
 * automatically by GameSnacks to join the room.
 * **Membership:** After joining, a player becomes a member, able to
send/receive
 * events and catch up.
 * An event is broadcast to all players, including the joining player.
 * **Leaving:** Once a player leaves (host or invitee), they can no longer
perform
 * actions or receive events.
 *
 * @param action - The action to perform (LivePresenceAction.JOIN or
 * LivePresenceAction.LEAVE).
 *
 * @return A Promise that resolves when the presence update request is sent
 * successfully.
 * Confirmation of the server-side processing is provided asynchronously
through the
 * `onPlayerPresenceUpdated` callback.
 */
 updatePlayerPresence(action: LivePresenceAction): Promise<void>;
 /**
 * Registers a callback to be called when a player's presence in the room is
updated
 * (joins or leaves).
 *
 * @param callback - The function to call when a player enters or exits the
room.
 * The callback receives a Promise that resolves to a LivePlayerDataEvent
object.
 */
 onPlayerPresenceUpdated(
 callback: (
 presenceEventPromise: Promise<LivePlayerDataEvent | DevSdkError>,
 ) => void,
 ): void;
 /**
 * Sends event data that will be relayed to other players connected to this
session.
 *
 * @param event - The event data to send.
 *
 * @return A Promise that resolves when the send-event request is sent
successfully.
 * Confirmation of the server-side processing is provided asynchronously
through the
 * `onEventsReceived` callback.
 */
 sendEvent(event: LiveEventData): Promise<void>;
 /**
 * Registers a callback function to handle incoming game events.
 * This includes all events, even those initiated by the player who triggered
them.
 *
 * Each event will include information about the action's sequence within the
current
 * game session, enabling the game to process events in the correct order.
 *
 * @param callback - The function to call when events are received.
 */
 onEventsReceived(
 callback: (eventsPromise: Promise<LiveEventData[]>) => void,
 ): void;
 /**
 * Synchronizes a player by providing all missing events starting from a
specific
 * action order.
 * If the provided `actionOrder` is older than 1 minute, the player is
considered out
 * of sync, and we will not return any events. Instead, the game should drop
the player
 * from the room.
 *
 * @param actionOrder - The last known action order received by the player.
 * Missing events will start from the next action order.
 *
 * @return A Promise that resolves to an array of missing events.
 */
 catchup(actionOrder: number): Promise<LiveEventData[]>;
 /**
 * Retrieves the URL of the current multiplayer room and copies it to the
user's.
 * This URL can be shared with other players to invite them to join the room.
 * This method only works for the Play with Friends scenario, it doesn't work
in
 * matchmaking mode.
 *
 * @return A Promise that resolves to the URL of the room.
 * Throws an error if the user is not currently in a room.
 */
 getRoomUrl(): Promise<string>;
}
